import React from 'react';
import {
  BrowserRouter as Router,
  Navigate,
  Route,
  Routes,
} from 'react-router-dom';

import AppShell from './components/layout/AppShell';
import LegacyRedirect from './components/layout/LegacyRedirect';
import SyncAuthBanner from './components/sync/SyncAuthBanner';

import { useAuth } from './auth/AuthProvider';

// Products
import ProductList from './components/ProductList';
import ProductDetail from './components/ProductDetail';
import CategorisationDashboard from './components/sync/CategorisationDashboard';
import CategoryTreeBrowser from './components/sync/CategoryTreeBrowser';
import NoCategoryQueue from './components/sync/NoCategoryQueue';
import BulkCategoriseView from './components/sync/BulkCategoriseView';
import ProductRetirement from './components/products/ProductRetirement';

// Customers
import CustomersPage from './components/customers/CustomersPage';
import CustomerDetailPage from './components/customers/CustomerDetailPage';

// Sales orders
import OrdersPage from './components/orders/OrdersPage';
import OrderDetailPage from './components/orders/OrderDetailPage';
import InvoicesPage from './components/invoices/InvoicesPage';
import InvoiceDetailPage from './components/invoices/InvoiceDetailPage';

// Credit
import CreditPage from './components/credit/CreditPage';

// Pricing
import PricingDashboard from './components/sync/PricingDashboard';
import PriceLookup from './components/sync/PriceLookup';
import PriceListViewer from './components/sync/PriceListViewer';

// Inventory
import InventoryDashboard from './components/inventory/InventoryDashboard';
import StockLevels from './components/inventory/StockLevels';
import StockDetail from './components/inventory/StockDetail';

// Jobs
import JobsDashboard from './components/jobs/JobsDashboard';
import JobsHistory from './components/jobs/JobsHistory';
import JobDetail from './components/sync/JobDetail';
import RunSync from './components/jobs/RunSync';
import SchedulerApp from './scheduler/SchedulerApp';
import DiffExplorer from './components/sync/DiffExplorer';
import SkuDetail from './components/sync/SkuDetail';
import ManualSyncs from './components/sync/ManualSyncs';
import CompanyCleanup from './components/sync/CompanyCleanup';
import OverridesList from './components/sync/OverridesList';
import MappingsEditor from './components/sync/MappingsEditor';
import ValueLookups from './components/sync/ValueLookups';
import CompanySettings from './components/sync/CompanySettings';
import PageGeneration from './components/pages/PageGeneration';
// PIM
import SchemaAdmin from './components/pim/SchemaAdmin';
import PimProducts from './components/pim/PimProducts';
import ProductEditor from './components/pim/ProductEditor';
import ImageReviewQueue from './components/pim/ImageReviewQueue';
import NameRebuild from './components/pim/NameRebuild';
import CategoryTrees from './components/pim/CategoryTrees';
import AutoCategorisation from './components/pim/AutoCategorisation';
import CategoryTreeEditor from './components/pim/CategoryTreeEditor';
import MappingsAdmin from './components/pim/MappingsAdmin';
import AkeneoPush from './components/pim/AkeneoPush';
import PimSettings from './components/pim/PimSettings';

// Approvals / Admin
import ApprovalsQueue from './components/approvals/ApprovalsQueue';
import ApprovalRules from './components/admin/ApprovalRules';
import Users from './components/admin/Users';
import Tags from './components/admin/Tags';

// Test
import TestCheckout from './components/test/TestCheckout';

// Shared styles for the remaining sync-era pages (config tools, forms, job detail).
import './components/sync/sync-pages.css';
import './styles/oc-base.css';
import './App.css';

/**
 * Main authenticated Waterco application.
 *
 * This component is rendered only after Keycloak authentication
 * has completed successfully.
 */
function AuthenticatedApplication() {
  return (
    <Router>
      <AppShell>
        <SyncAuthBanner />

        <Routes>
          {/* Default */}
          <Route
            path="/"
            element={<Navigate to="/products" replace />}
          />

          {/* Customers */}
          <Route
            path="/customers"
            element={<CustomersPage />}
          />

          <Route
            path="/customers/:company/:custNum"
            element={<CustomerDetailPage />}
          />

          {/* Sales Orders */}
          <Route
            path="/orders"
            element={<OrdersPage />}
          />

          <Route
            path="/orders/:company/:orderNum"
            element={<OrderDetailPage />}
          />

          {/* Invoices */}
          <Route
            path="/invoices"
            element={<InvoicesPage />}
          />

          <Route
            path="/invoices/:company/:invoiceNum"
            element={<InvoiceDetailPage />}
          />

          {/* Credit */}
          <Route
            path="/credit"
            element={<CreditPage />}
          />

          {/* PIM */}
          <Route
            path="/pim"
            element={
              <Navigate
                to="/pim/products"
                replace
              />
            }
          />

          <Route
            path="/pim/products"
            element={<PimProducts />}
          />

          <Route
            path="/pim/products/:sku"
            element={<ProductEditor />}
          />

          <Route
            path="/pim/image-review"
            element={<ImageReviewQueue />}
          />

          <Route
            path="/pim/name-rebuild"
            element={<NameRebuild />}
          />

          <Route
            path="/pim/categories"
            element={<CategoryTrees />}
          />

          <Route
            path="/pim/auto-categorisation"
            element={<AutoCategorisation />}
          />

          <Route
            path="/pim/categories/:code"
            element={<CategoryTreeEditor />}
          />

          <Route
            path="/pim/approvals"
            element={<ApprovalsQueue />}
          />

          <Route
            path="/pim/approvals/rules"
            element={<ApprovalRules />}
          />

          <Route
            path="/pim/schema"
            element={<SchemaAdmin />}
          />

          <Route
            path="/pim/mappings"
            element={<MappingsAdmin />}
          />

          <Route
            path="/pim/akeneo-push"
            element={<AkeneoPush />}
          />

          <Route
            path="/pim/settings"
            element={<PimSettings />}
          />

          {/* Products */}
          <Route
            path="/products"
            element={<ProductList />}
          />

          <Route
            path="/products/categorisation"
            element={<CategorisationDashboard />}
          />

          <Route
            path="/products/categorisation/no-category"
            element={<NoCategoryQueue />}
          />

          <Route
            path="/products/categorisation/tree"
            element={<CategoryTreeBrowser />}
          />

          <Route
            path="/products/categorisation/bulk"
            element={<BulkCategoriseView />}
          />

          {/* Keep this route before /products/:id */}
          <Route
            path="/products/retire"
            element={<ProductRetirement />}
          />

          <Route
            path="/products/:id"
            element={<ProductDetail />}
          />

          {/* Pricing */}
          <Route
            path="/pricing"
            element={<PricingDashboard />}
          />

          <Route
            path="/pricing/lookup"
            element={<PriceLookup />}
          />

          <Route
            path="/pricing/price-lists"
            element={<PriceListViewer />}
          />

          {/* Inventory */}
          <Route
            path="/inventory"
            element={<InventoryDashboard />}
          />

          <Route
            path="/inventory/stock"
            element={<StockLevels />}
          />

          <Route
            path="/inventory/stock/:company/:partNum"
            element={<StockDetail />}
          />

          <Route
            path="/inventory/warehouses"
            element={
              <Navigate
                to="/configuration?tab=warehouses"
                replace
              />
            }
          />

          {/* Manual Syncs */}
          <Route
            path="/manual-syncs"
            element={<ManualSyncs />}
          />

          <Route
            path="/manual-syncs/company-cleanup"
            element={<CompanyCleanup />}
          />

          <Route
            path="/company-cleanup"
            element={
              <LegacyRedirect
                to="/manual-syncs/company-cleanup"
              />
            }
          />

          {/* Jobs */}
          <Route
            path="/jobs"
            element={<JobsDashboard />}
          />

          <Route
            path="/jobs/history"
            element={<JobsHistory />}
          />

          <Route
            path="/jobs/history/:jobId"
            element={<JobDetail />}
          />

          <Route
            path="/jobs/run"
            element={<RunSync tab="bigcommerce" />}
          />

          <Route
            path="/jobs/run/epicor"
            element={<RunSync tab="epicor" />}
          />

          <Route
            path="/jobs/schedules/*"
            element={<SchedulerApp />}
          />

          {/* Job Configuration */}
          <Route
            path="/jobs/config/diffs"
            element={<DiffExplorer />}
          />

          <Route
            path="/jobs/config/diffs/:market/:sku"
            element={<SkuDetail />}
          />

          <Route
            path="/jobs/config/overrides"
            element={<OverridesList />}
          />

          <Route
            path="/jobs/config/mappings"
            element={<MappingsEditor />}
          />

          <Route
            path="/jobs/config/value-lookups"
            element={<ValueLookups />}
          />

          {/* AI page generation */}
          <Route
            path="/ai-pages"
            element={<PageGeneration />}
          />

          {/* Legacy company-settings route */}
          <Route
            path="/jobs/config/company-settings"
            element={
              <LegacyRedirect
                to="/configuration"
              />
            }
          />

            
          {/* Configuration */}
          <Route
            path="/configuration"
            element={<CompanySettings />}
          />

          <Route
            path="/configuration/users"
            element={<Users />}
          />

          <Route
            path="/configuration/tags"
            element={<Tags />}
          />

          {/* Test */}
          <Route
            path="/test/checkout"
            element={<TestCheckout />}
          />

          {/* Legacy /sync Redirects */}
          <Route
            path="/sync"
            element={<LegacyRedirect to="/jobs" />}
          />

          <Route
            path="/sync/dashboard"
            element={<LegacyRedirect to="/jobs" />}
          />

          <Route
            path="/sync/jobs"
            element={
              <LegacyRedirect
                to="/jobs/history"
              />
            }
          />

          <Route
            path="/sync/jobs/:jobId"
            element={
              <LegacyRedirect
                to={(params) =>
                  `/jobs/history/${params.jobId}`
                }
              />
            }
          />

          <Route
            path="/sync/manual-sync"
            element={
              <LegacyRedirect
                to="/jobs/run"
              />
            }
          />

          <Route
            path="/sync/epicor-sync"
            element={
              <LegacyRedirect
                to="/jobs/run/epicor"
              />
            }
          />

          <Route
            path="/sync/epicor-customers"
            element={
              <LegacyRedirect
                to="/customers"
              />
            }
          />

          <Route
            path="/sync/pricing"
            element={
              <LegacyRedirect
                to="/pricing"
              />
            }
          />

          <Route
            path="/sync/pricing/lookup"
            element={
              <LegacyRedirect
                to="/pricing/lookup"
              />
            }
          />

          <Route
            path="/sync/diffs"
            element={
              <LegacyRedirect
                to="/jobs/config/diffs"
              />
            }
          />

          <Route
            path="/sync/diffs/:market/:sku"
            element={
              <LegacyRedirect
                to={(params) =>
                  `/jobs/config/diffs/${
                    params.market
                  }/${encodeURIComponent(params.sku)}`
                }
              />
            }
          />

          <Route
            path="/sync/overrides"
            element={
              <LegacyRedirect
                to="/jobs/config/overrides"
              />
            }
          />

          <Route
            path="/sync/mappings"
            element={
              <LegacyRedirect
                to="/jobs/config/mappings"
              />
            }
          />

          <Route
            path="/sync/value-lookups"
            element={
              <LegacyRedirect
                to="/jobs/config/value-lookups"
              />
            }
          />

          <Route
            path="/sync/categorisation"
            element={
              <LegacyRedirect
                to="/products/categorisation"
              />
            }
          />

          <Route
            path="/sync/categorisation/no-category"
            element={
              <LegacyRedirect
                to="/products/categorisation/no-category"
              />
            }
          />

          <Route
            path="/sync/categorisation/tree"
            element={
              <LegacyRedirect
                to="/products/categorisation/tree"
              />
            }
          />

          <Route
            path="/sync/categorisation/bulk"
            element={
              <LegacyRedirect
                to="/products/categorisation/bulk"
              />
            }
          />

          <Route
            path="/sync/*"
            element={
              <LegacyRedirect
                to="/jobs"
              />
            }
          />

          {/* Legacy Scheduler Redirects */}
          <Route
            path="/scheduler"
            element={
              <LegacyRedirect
                to="/jobs/schedules"
              />
            }
          />

          <Route
            path="/scheduler/new"
            element={
              <LegacyRedirect
                to="/jobs/schedules/new"
              />
            }
          />

          <Route
            path="/scheduler/:id/edit"
            element={
              <LegacyRedirect
                to={(params) =>
                  `/jobs/schedules/${params.id}/edit`
                }
              />
            }
          />

          <Route
            path="/scheduler/:id"
            element={
              <LegacyRedirect
                to={(params) =>
                  `/jobs/schedules/${params.id}`
                }
              />
            }
          />

          {/* Unknown Route */}
          <Route
            path="*"
            element={
              <Navigate
                to="/products"
                replace
              />
            }
          />
        </Routes>
      </AppShell>
    </Router>
  );
}

/**
 * Displayed while Keycloak is checking the existing login session.
 */
function AuthenticationLoader() {
  return (
    <div className="authentication-loader">
      <div className="authentication-loader__content">
        <h2>Waterco Middleware</h2>
        <p>Checking authentication...</p>
      </div>
    </div>
  );
}

/**
 * Displayed if Keycloak authentication fails.
 */
function AuthenticationError({
  error,
  onLogin,
}) {
  const message =
    error?.message ||
    'Authentication could not be completed.';

  return (
    <div className="authentication-loader">
      <div className="authentication-loader__content">
        <h2>Waterco Middleware</h2>
        <p>{message}</p>
        <button
          type="button"
          onClick={onLogin}
        >
          Sign in again
        </button>
      </div>
    </div>
  );
}

/**
 * Root application.
 *
 * AuthProvider should already wrap this App component in index.js.
 */
function App() {
  const {
    initialized,
    authenticated,
    authError,
    login,
  } = useAuth();

  if (!initialized) {
    return <AuthenticationLoader />;
  }

  if (authError || !authenticated) {
    return (
      <AuthenticationError
        error={authError}
        onLogin={login}
      />
    );
  }

  return <AuthenticatedApplication />;
}

export default App;